import { type ComponentType } from 'react';

import { Navigate } from 'react-router-dom';

import { AuthState } from '@/shared/authentication';
import { useAuthorize } from '@/shared/authorization';

import LoadingIndicator from '@/components/shared/LoadingIndicator/LoadingIndicator';

import useQuery from '@/hooks/useQuery';

import { ADMIN_SECTIONS, visibleAdminSections, type AdminSectionSlug } from './admin-sections';
import { lazyRoute } from './lazy-route';

import type { IconName } from '@bozic/ui/icon';

const sectionImports: Record<AdminSectionSlug, () => Promise<{ default: ComponentType }>> = {
  'insurance-agencies': () =>
    import('./admin-section-shells').then(m => ({ default: m.InsuranceAgenciesShell })),
  'insurance-agents': () =>
    import('./admin-section-shells').then(m => ({ default: m.InsuranceAgentsShell })),
  operations: () => import('./admin-section-shells').then(m => ({ default: m.OperationsShell })),
  producers: () => import('./admin-section-shells').then(m => ({ default: m.ProducersShell })),
  stakeholders: () =>
    import('./admin-section-shells').then(m => ({ default: m.StakeholdersShell })),
  // tag-types + agency-licenses: their feature index exports are already React.lazy —
  // wrapping those again breaks rendering, so import the underlying components.
  'tag-types': () => import('@/features/admin-panel/tag-types/components/TagTypes'),
  'agency-licenses': () =>
    import('@/features/admin-panel/agency-licenses/components/AgencyLicenses'),
  'user-agreements': () => import('@/features/admin-panel/user-agreements'),
  'aip-binding': () => import('@/features/admin-panel/aip-binding/components/AipBinding'),
  'price-reports': () => import('@/features/admin-panel/price-reports')
};

// Sidebar icons: nearest @bozic/ui equivalents of the legacy left-menu icons
// (AdminSection.icon carries react-icons components the nav handle can't use).
const NAV_ICONS: Record<AdminSectionSlug, IconName> = {
  'insurance-agencies': 'FolderShared',
  'insurance-agents': 'ManageAccounts',
  operations: 'Barn',
  producers: 'Agriculture',
  stakeholders: 'SocialDistance',
  'tag-types': 'Label',
  'agency-licenses': 'ShieldCheckered',
  'user-agreements': 'EditNote',
  'aip-binding': 'DatasetLinked',
  'price-reports': 'Assessment'
};

// `/admin` index: land on the role's first visible section. Legacy `/admin?id=N`
// bookmarks are a canonical alias kept indefinitely (rollout strategy §4): N indexes
// the same role-filtered order the old left menu used, so it resolves per role here.
const AdminIndexRedirect = () => {
  const { role } = useAuthorize();
  const { id } = useQuery();
  // Redirecting before OIDC resolves would compute the wrong role-filtered order.
  if (role === AuthState.initialization) return <LoadingIndicator />;
  const visible = visibleAdminSections(role);
  const target = visible[Number(id)] ?? visible[0];
  return <Navigate to={target.slug} replace />;
};

// /admin
export const adminRoutes = lazyRoute(
  '/admin',
  () => import('@/modules/AdminModule').then(m => ({ default: m.AdminModule })),
  {
    children: [
      { index: true, element: <AdminIndexRedirect /> },
      ...ADMIN_SECTIONS.map(section =>
        lazyRoute(section.slug, sectionImports[section.slug], {
          handle: { label: section.label, icon: NAV_ICONS[section.slug], gate: section.gate }
        })
      )
    ]
  }
);
